import { useCallback } from "react";

/* ================================
   TIPOS
================================= */

export interface PersonBlock {
    name?: string;
    nit?: string;
    address?: string;
    city?: string;
    tel?: string;
    email?: string;
}

export interface ZplGuideData {
    header: {
        logoUrl?: string;
        barcode: string;
    };
    route?: string;
    weight?: number;
    recipient?: PersonBlock;
    sender?: PersonBlock;
    state?: string;
}

export interface GuideOptions {
    DPI: 203 | 300;
    PaperWidthMM: number;
    LabelWidthMM: number;
    LabelHeightMM: number;
    LabelsPerRow: number;
    HorizontalGapMM?: number;
    PrintLogo?: boolean;
    LogoWidthMM?: number;
    PrintBarcode?: boolean;
    BarcodeHeightMM?: number;
    BarcodeModuleWidth?: number;
    Darkness?: number;
    DefaultSpacingMM?: number;
    copies?: number;
}

/* ================================
   UTILIDAD IMAGEN A ZPL
================================= */

async function imageUrlToZpl(url: string, targetWidthDots: number) {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = url;

    await new Promise<void>((res, rej) => {
        img.onload = () => res();
        img.onerror = () => rej(new Error("Error cargando imagen"));
    });

    const scale = targetWidthDots / img.width;
    const width = targetWidthDots;
    const height = Math.floor(img.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas error");

    ctx.drawImage(img, 0, 0, width, height);

    const imgData = ctx.getImageData(0, 0, width, height).data;
    const bytesPerRow = Math.ceil(width / 8);

    let hex = "";
    const threshold = 180;

    for (let y = 0; y < height; y++) {
        for (let xb = 0; xb < bytesPerRow; xb++) {
            let byte = 0;

            for (let bit = 0; bit < 8; bit++) {
                const x = xb * 8 + bit;
                if (x >= width) continue;

                const idx = (y * width + x) * 4;
                const r = imgData[idx];
                const g = imgData[idx + 1];
                const b = imgData[idx + 2];

                const gray = 0.299 * r + 0.587 * g + 0.114 * b;

                if (gray < threshold) byte |= (0x80 >> bit);
            }

            hex += byte.toString(16).padStart(2, "0").toUpperCase();
        }
    }

    const totalBytes = bytesPerRow * height;

    return {
        zpl: `^GFA,${totalBytes},${totalBytes},${bytesPerRow},${hex}`,
        height,
    };
}

/* ================================
   HOOK GENERADOR
================================= */

export function useZplGuideGenerator() {

    const generateGuideZpl = useCallback(
        async (data: ZplGuideData, props: GuideOptions) => {

            const mmToDots = (mm: number) =>
                Math.round((mm / 25.4) * props.DPI);

            const labelWidthDots = mmToDots(props.LabelWidthMM);
            const labelHeightDots = mmToDots(props.LabelHeightMM);

            const marginX = mmToDots(4);
            let currentY = mmToDots(6);

            let zplBody = "";

            /* ================= FUNCION DIBUJAR LINEA ================= */

            const drawLine = (
                text: string,
                fontSize: number,
                lineHeightRatio: number = 1.12
            ) => {
                zplBody += `
^CF0,${fontSize}
^FO${marginX},${currentY}
^FD${text}^FS
`;
                currentY += Math.round(fontSize * lineHeightRatio);
            };

            /* ================= FUNCION BLOQUE TEXTO ================= */

            const drawBlock = (
                text: string,
                fontSize: number,
                maxWidth: number,
                maxLines: number = 2,
                lineSpacing: number = 6
            ) => {
                zplBody += `
^CF0,${fontSize}
^FO${marginX},${currentY}
^FB${maxWidth},${maxLines},${lineSpacing},L,0
^FD${text}^FS
`;
                currentY += Math.round(fontSize * maxLines * 1.1);
            };

            /* ================= HEADER ================= */

            const headerStartY = currentY;
            let headerHeight = 0;

            if (props.PrintLogo && data.header.logoUrl) {

                const logoWidthDots = mmToDots(props.LogoWidthMM ?? 35);

                const logo = await imageUrlToZpl(
                    data.header.logoUrl,
                    logoWidthDots
                );

                zplBody += `
^FO${marginX},${headerStartY}
${logo.zpl}
^FS
`;

                headerHeight = Math.max(headerHeight, logo.height);
            }

            /* ================= BARCODE ================= */

            let barcodeTotalHeight = 0;

            if (props.PrintBarcode) {

                const barcodeHeightDots = mmToDots(props.BarcodeHeightMM ?? 20);
                const barcodeX = labelWidthDots - marginX - mmToDots(30);

                zplBody += `
^FO${barcodeX},${headerStartY}
^BY${props.BarcodeModuleWidth ?? 2}
^BCN,${barcodeHeightDots},N,N,N
^FD${String(data.header.barcode ?? "")}^FS
`;

                barcodeTotalHeight = barcodeHeightDots;

                if (data.weight) {

                    const weightFont = 28;
                    const weightY = headerStartY + barcodeHeightDots + 6;

                    zplBody += `
^CF0,${weightFont}
^FO${barcodeX},${weightY}
^FD(${data.weight} KG)^FS
`;

                    barcodeTotalHeight += Math.round(weightFont * 1.15);
                }

                headerHeight = Math.max(headerHeight, barcodeTotalHeight);
            }

            currentY = headerStartY + headerHeight + mmToDots(4);

            /* ================= RUTA ================= */

            if (data.route) {

                const routeWidth =
                    labelWidthDots - marginX - mmToDots(35);

                drawBlock(
                    data.route,
                    40,
                    routeWidth,
                    2
                );

                currentY += 4;
            }

            /* ================= DESTINATARIO ================= */

            if (data.recipient) {

                drawLine("Destinatario:", 28, 1.05);
                drawLine(data.recipient.name ?? "", 26, 1.05);

                if (data.recipient.address) {
                    drawLine(data.recipient.address, 24, 1.05);
                }

                drawLine(`Tel: ${data.recipient.tel ?? ""}`, 24, 1.15);
            }

            /* ================= REMITENTE ================= */

            if (data.sender) {

                currentY += 4;

                drawLine("Remitente:", 28, 1.05);
                drawLine(data.sender.name ?? "", 26, 1.05);
                drawLine(`Tel: ${data.sender.tel ?? ""}`, 24, 1.15);
            }

            /* ================= ESTADO ================= */

            if (data.state) {

                currentY += 6;
                drawLine(`Estado: ${data.state}`, 26, 1.1);
            }

            /* ================= FINAL ================= */

            return `
^XA
^PW${labelWidthDots}
^LL${labelHeightDots}
^CI28
^MD${props.Darkness ?? 20}
^PQ${props.copies ?? 1},0,0,N
${zplBody}
^XZ
`.trim();
        },
        []
    );

    return { generateGuideZpl };
}